import _, {memoize} from 'lodash';

import {
  CachingTraceServerClient,
  TraceTableRowQueryReq,
  TraceTableRowQueryRes,
} from './traceServerCachingClient';
import {
  CompletionsCreateReq,
  CompletionsCreateRes,
  CompletionsCreateStreamReq,
  CompletionsCreateStreamRes,
  FeedbackCreateReq,
  FeedbackCreateRes,
  FeedbackPurgeReq,
  FeedbackPurgeRes,
  FeedbackReplaceReq,
  FeedbackReplaceRes,
  TableCreateReq,
  TableCreateRes,
  TableUpdateReq,
  TableUpdateRes,
  TraceCallsDeleteReq,
  TraceCallUpdateReq,
  TraceObjCreateReq,
  TraceObjCreateRes,
  TraceObjAddTagsReq,
  TraceObjAddTagsRes,
  TraceObjDeleteReq,
  TraceObjDeleteRes,
  TraceObjRemoveAliasesReq,
  TraceObjRemoveAliasesRes,
  TraceObjRemoveTagsReq,
  TraceObjRemoveTagsRes,
  TraceObjSetAliasesReq,
  TraceObjSetAliasesRes,
  TraceRefsReadBatchReq,
  TraceRefsReadBatchRes,
  TraceTableQueryReq,
  TraceTableQueryRes,
} from './traceServerClientTypes';

const DEFAULT_BATCH_INTERVAL = 150;
const MAX_REFS_PER_BATCH = 1000;

export class TraceServerClient extends CachingTraceServerClient {
  private readBatchCollectors: Array<{
    req: TraceRefsReadBatchReq;
    resolvePromise: (res: TraceRefsReadBatchRes) => void;
    rejectPromise: (err: any) => void;
  }> = [];
  private onDeleteListeners: Array<() => void>;
  private onRenameListeners: Array<() => void>;
  private onFeedbackListeners: Record<string, Array<() => void>>;
  private onObjectListeners: Array<() => void>;
  private batchObjectChangesDepth = 0;
  private hasPendingObjectNotification = false;

  // Similar to the readBatchCollectors, but for tableRowQuery.
  // The fundamental idea here is that some tableRowQuery requests
  // can be batched together, reducing the number of requests we
  // make to the trace server.
  private tableRowQueryCollectors: Array<{
    req: TraceTableRowQueryReq;
    resolvePromise: (res: TraceTableRowQueryRes) => void;
    rejectPromise: (err: any) => void;
  }> = [];

  constructor(baseUrl: string) {
    super(baseUrl);
    this.readBatchCollectors = [];
    this.scheduleReadBatch();
    this.onDeleteListeners = [];
    this.onRenameListeners = [];
    this.onFeedbackListeners = {};
    this.onObjectListeners = [];
    this.tableRowQueryCollectors = [];
  }

  /**
   * Registers a callback to be called when a delete operation occurs.
   * This method is purely for local notification within the client
   *    and does not interact with the REST API.
   *
   * @param callback A function to be called when a delete operation is triggered.
   * @returns A function to unregister the callback.
   */
  public registerOnDeleteListener(callback: () => void): () => void {
    this.onDeleteListeners.push(callback);
    return () => {
      this.onDeleteListeners = this.onDeleteListeners.filter(
        listener => listener !== callback
      );
    };
  }
  public registerOnRenameListener(callback: () => void): () => void {
    this.onRenameListeners.push(callback);
    return () => {
      this.onRenameListeners = this.onRenameListeners.filter(
        listener => listener !== callback
      );
    };
  }
  public registerOnFeedbackListener(
    weaveRef: string,
    callback: () => void
  ): () => void {
    if (!(weaveRef in this.onFeedbackListeners)) {
      this.onFeedbackListeners[weaveRef] = [];
    }
    this.onFeedbackListeners[weaveRef].push(callback);
    return () => {
      const newListeners = this.onFeedbackListeners[weaveRef].filter(
        listener => listener !== callback
      );
      if (newListeners.length) {
        this.onFeedbackListeners[weaveRef] = newListeners;
      } else {
        delete this.onFeedbackListeners[weaveRef];
      }
    };
  }

  public registerOnObjectListener(callback: () => void): () => void {
    this.onObjectListeners.push(callback);
    return () => {
      this.onObjectListeners = this.onObjectListeners.filter(
        listener => listener !== callback
      );
    };
  }

  /** Suppress object-change notifications until the matching `endBatchObjectChanges`. Nestable. */
  public beginBatchObjectChanges(): void {
    this.batchObjectChangesDepth++;
  }

  /** Decrement the batch depth; fires deferred notifications when it reaches zero. */
  public endBatchObjectChanges(): void {
    this.batchObjectChangesDepth--;
    if (
      this.batchObjectChangesDepth === 0 &&
      this.hasPendingObjectNotification
    ) {
      this.hasPendingObjectNotification = false;
      this.onObjectListeners.forEach(listener => listener());
    }
  }

  /** Notify object listeners, or defer if inside a batch. */
  private notifyObjectListeners(): void {
    if (this.batchObjectChangesDepth > 0) {
      this.hasPendingObjectNotification = true;
    } else {
      this.onObjectListeners.forEach(listener => listener());
    }
  }

  public callsDelete(req: TraceCallsDeleteReq): Promise<void> {
    const res = super.callsDelete(req).then(() => {
      this.onDeleteListeners.forEach(listener => listener());
    });
    return res;
  }

  public callUpdate(req: TraceCallUpdateReq): Promise<void> {
    const res = super.callUpdate(req).then(() => {
      this.onRenameListeners.forEach(listener => listener());
    });
    return res;
  }

  public objCreate(req: TraceObjCreateReq): Promise<TraceObjCreateRes> {
    const res = super.objCreate(req).then(createRes => {
      this.notifyObjectListeners();
      return createRes;
    });
    return res;
  }

  public tableUpdate(req: TableUpdateReq): Promise<TableUpdateRes> {
    return super.tableUpdate(req);
  }

  public tableCreate(req: TableCreateReq): Promise<TableCreateRes> {
    return super.tableCreate(req);
  }

  public feedbackCreate(req: FeedbackCreateReq): Promise<FeedbackCreateRes> {
    const res = super.feedbackCreate(req).then(createRes => {
      const listeners = this.onFeedbackListeners[req.weave_ref] ?? [];
      listeners.forEach(listener => listener());
      return createRes;
    });
    return res;
  }
  public feedbackPurge(req: FeedbackPurgeReq): Promise<FeedbackPurgeRes> {
    const res = super.feedbackPurge(req).then(purgeRes => {
      // TODO: Since purge takes a query, we need to change the result to include
      //       information about the refs that were modified.
      //       For now, just call all registered feedback listeners.
      for (const listeners of Object.values(this.onFeedbackListeners)) {
        listeners.forEach(listener => listener());
      }
      return purgeRes;
    });
    return res;
  }
  public feedbackReplace(req: FeedbackReplaceReq): Promise<FeedbackReplaceRes> {
    const res = super.feedbackReplace(req).then(replaceRes => {
      const listeners = this.onFeedbackListeners[req.weave_ref] ?? [];
      listeners.forEach(listener => listener());
      return replaceRes;
    });
    return res;
  }

  public objDelete(req: TraceObjDeleteReq): Promise<TraceObjDeleteRes> {
    const res = super.objDelete(req).then(r => {
      this.notifyObjectListeners();
      return r;
    });
    return res;
  }

  public objAddTags(req: TraceObjAddTagsReq): Promise<TraceObjAddTagsRes> {
    return super.objAddTags(req).then(r => {
      this.onObjectListeners.forEach(listener => listener());
      return r;
    });
  }

  public objRemoveTags(
    req: TraceObjRemoveTagsReq
  ): Promise<TraceObjRemoveTagsRes> {
    return super.objRemoveTags(req).then(r => {
      this.onObjectListeners.forEach(listener => listener());
      return r;
    });
  }

  public objSetAliases(
    req: TraceObjSetAliasesReq
  ): Promise<TraceObjSetAliasesRes> {
    return super.objSetAliases(req).then(r => {
      this.onObjectListeners.forEach(listener => listener());
      return r;
    });
  }

  public objRemoveAliases(
    req: TraceObjRemoveAliasesReq
  ): Promise<TraceObjRemoveAliasesRes> {
    return super.objRemoveAliases(req).then(r => {
      this.onObjectListeners.forEach(listener => listener());
      return r;
    });
  }

  public readBatch(req: TraceRefsReadBatchReq): Promise<TraceRefsReadBatchRes> {
    return this.requestReadBatch(req);
  }

  public completionsCreate(
    req: CompletionsCreateReq
  ): Promise<CompletionsCreateRes> {
    return super.completionsCreate(req);
  }

  public completionsCreateStream(
    req: CompletionsCreateStreamReq,
    onChunk?: (chunk: any) => void,
    signal?: AbortSignal
  ): Promise<CompletionsCreateStreamRes> {
    return super.completionsCreateStream(req, onChunk, signal);
  }

  public override tableQuery(
    req: TraceTableQueryReq
  ): Promise<TraceTableQueryRes> {
    // Specific case for optimization:
    if (tableQuerySuitableForBatching(req)) {
      return this.tableRowQuery({
        project_id: req.project_id,
        digest: req.digest,
        row_digests: req.filter.row_digests,
      });
    }
    return super.tableQuery(req);
  }

  public tableRowQuery(
    req: TraceTableRowQueryReq
  ): Promise<TraceTableRowQueryRes> {
    // Rather than immediately making a request, we add the request to a
    // queue of pending requests which will be batched together.
    return new Promise<TraceTableRowQueryRes>((resolve, reject) => {
      this.tableRowQueryCollectors.push({
        req,
        resolvePromise: resolve,
        rejectPromise: reject,
      });
      this.ensureTableRowQueryBatchLoopStarted();
    });
  }

  private requestReadBatch(
    req: TraceRefsReadBatchReq
  ): Promise<TraceRefsReadBatchRes> {
    return new Promise<TraceRefsReadBatchRes>((resolve, reject) => {
      this.readBatchCollectors.push({
        req,
        resolvePromise: resolve,
        rejectPromise: reject,
      });
    });
  }

  private async doReadBatch() {
    if (this.readBatchCollectors.length === 0) {
      return;
    }
    const collectors = [...this.readBatchCollectors];
    this.readBatchCollectors = [];

    const valMap = new Map<string, any>();
    try {
      const refs = _.uniq(collectors.map(c => c.req.refs).flat());
      while (refs.length > 0) {
        const refsForBatch = refs.splice(0, MAX_REFS_PER_BATCH);
        const res = await this.readBatchDirect({refs: refsForBatch});
        const vals = res.vals;
        for (let i = 0; i < refsForBatch.length; i++) {
          valMap.set(refsForBatch[i], vals[i]);
        }
      }
    } catch (error) {
      // Reject all waiting promises with the error
      collectors.forEach(collector => {
        collector.rejectPromise(error);
      });
    }
    collectors.forEach(collector => {
      const req = collector.req;
      const refVals = req.refs.map(ref => valMap.get(ref));
      collector.resolvePromise({vals: refVals});
    });
  }

  private async doTableRowQuery() {
    // This is the workhorse for the tableRowQuery batcher.
    // We create N groups of results (where N is the number of table digests),
    // Then for each group, we make a single tableRowQuery request to the
    // trace server, and then use the results to populate the results for
    // each of the individual requests.
    const collectors = [...this.tableRowQueryCollectors];
    this.tableRowQueryCollectors = [];
    if (collectors.length === 0) {
      return;
    }
    const reqs = collectors.map(c => c.req);
    const groupedReqs = _.groupBy(
      reqs,
      req => `${req.project_id}:{req.digest}`
    );
    await Promise.all(
      Object.entries(groupedReqs).map(async ([key, reqs]) => {
        const uniqueDigests = _.uniq(reqs.map(r => r.row_digests).flat());
        const res = await super.tableRowQuery({
          ...reqs[0],
          row_digests: uniqueDigests,
        });
        const digestMap = new Map<string, any>();
        res.rows.forEach(r => {
          digestMap.set(r.digest, r.val);
        });

        // each collector is guaranteed to get data for all its row digests,
        // nothing will be missed, because several collectors who share the
        // same table are fetched in a single request
        collectors.forEach(collector => {
          const rows = collector.req.row_digests.map(digest => ({
            digest,
            val: digestMap.get(digest),
          }));
          collector.resolvePromise({rows});
        });
      })
    );
  }

  private async scheduleReadBatch() {
    await this.doReadBatch();
    setTimeout(this.scheduleReadBatch.bind(this), DEFAULT_BATCH_INTERVAL);
  }

  private ensureTableRowQueryBatchLoopStarted = memoize(() => {
    // Responsible for scheduling the next batch of tableRowQuery requests.
    const iter = async () => {
      await this.doTableRowQuery();
      setTimeout(iter, DEFAULT_BATCH_INTERVAL);
    };
    iter();
  });

  private readBatchDirect(
    req: TraceRefsReadBatchReq
  ): Promise<TraceRefsReadBatchRes> {
    return super.readBatch(req);
  }
}

const tableQuerySuitableForBatching = (
  req: TraceTableQueryReq
): req is TraceTableQueryReq & {
  filter: {
    row_digests: NonNullable<
      NonNullable<TraceTableQueryReq['filter']>['row_digests']
    >;
  };
} => {
  // If the request does not contain a row_digests filter, return false
  if (!req.filter?.row_digests) {
    return false;
  }

  // If the request contains anything other than project_id, digest, and filter, return false
  // We can't support sorting/pagination, etc.
  if (
    !Object.entries(req).every(
      ([k, v]) => ['project_id', 'digest', 'filter'].includes(k) || v == null
    )
  ) {
    return false;
  }

  // If the filter contains anything other than row_digests, return false
  // Forward protection (there are no other filter properties yet)
  if (
    !Object.entries(req.filter).every(
      ([k, v]) => k === 'row_digests' || v == null
    )
  ) {
    return false;
  }
  return true;
};
